"use client"
import React, { useCallback } from 'react'
import { apiOutUrl } from '@/apis/user'
import { extractOfferCodeFromUrl } from '@/constants/hooks'
import Link from 'next/link'

interface Props {
  outUrl: string;
  merchantHref: string;
  unique_id: string;
  children: React.ReactNode;
  customClass?: string;
  domain: string;
  couponCode?: string;
}

const Theme18OfferLink = ({ outUrl, merchantHref, unique_id, children, customClass, domain, couponCode }: Props) => {

  const rawUrl = outUrl || merchantHref || '#'
  const isExternal = rawUrl.startsWith('http://') || rawUrl.startsWith('https://')
  const absoluteOutUrl = isExternal
    ? rawUrl
    : rawUrl === '#'
      ? '#'
      : `/${rawUrl.replace(/^\/+/, '')}`

  const offerCode = !isExternal && absoluteOutUrl !== '#' ? extractOfferCodeFromUrl(absoluteOutUrl) : ''

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      // 1. Copy coupon code to clipboard
      if (couponCode) {
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(couponCode);
          } else {
            const textArea = document.createElement("textarea");
            textArea.value = couponCode;
            textArea.style.position = "fixed";
            textArea.style.top = "0";
            textArea.style.left = "0";
            const target = document.body || document.documentElement;
            if (target) {
              target.appendChild(textArea);
              textArea.focus();
              textArea.select();
              document.execCommand('copy');
              target.removeChild(textArea);
            }
          }
        } catch (err) {
          console.error("Failed to copy code: ", err);
        }
      }

      // 2. Open merchantHref (store page) in a new tab with show modal parameters
      if (merchantHref && typeof window !== 'undefined') {
        try {
          const origin = window.location.origin;
          const cleanMerchantPath = merchantHref.startsWith('http://') || merchantHref.startsWith('https://')
            ? merchantHref
            : merchantHref.startsWith('/')
              ? `${origin}${merchantHref}`
              : `${origin}/${merchantHref}`;

          const newTabUrl = new URL(cleanMerchantPath);
          if (unique_id) {
            newTabUrl.searchParams.set('show', 'true');
            newTabUrl.searchParams.set('p_id', unique_id);
          }
          window.open(newTabUrl.toString(), '_blank', 'noopener,noreferrer');
        } catch (err) {
          console.error("Failed to construct store tab URL:", err);
          if (merchantHref) {
            window.open(merchantHref, '_blank', 'noopener,noreferrer');
          }
        }
      }

      // 3. Redirect the current tab to the affiliate store link
      try {
        if (offerCode && domain) {
          const response = await apiOutUrl(domain, offerCode);
          const redirectUrl = response?.data;

          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
        }

        if (isExternal && rawUrl !== '#') {
          window.location.href = rawUrl;
        }
      } catch (error) {
        console.error("Error getting redirect URL", error);
        if (isExternal && rawUrl !== '#') {
          window.location.href = rawUrl;
        }
      }
    },
    [merchantHref, unique_id, offerCode, couponCode, domain, isExternal, rawUrl]
  );

  return (
    <Link
      href={absoluteOutUrl}
      className={customClass}
      target="_blank"
      rel="nofollow sponsored noopener noreferrer"
      onClick={handleClick}
    >
      {children}
    </Link>
  )
}

export default Theme18OfferLink;
